import { useEffect, useRef, useState } from "react";
import io, { Socket } from "socket.io-client";
import { useList } from "../ui/listHooks";
import useItemLocations from "../api/useItemLocations";
import { ADD_ITEM_PROGRESS_CHANNEL } from "../../components/addItem/AddItemUtils";
import { MondayApiQueryInstance } from "@services/monday/monday-service-ts";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

interface SocketIOMessage {
  message: string;
  action: string;
  accountId?: string;
  itemId?: string;
  userId?: string;
  targetGroupId?: string;
  targetBoardId?: string;
  type?: string;
  createdTargetItemIds?: string[];
}

const broadcastProgressMessage = (type: string, createdTargetItemIds: string[] = []) => {
  try {
    const ch = new BroadcastChannel(ADD_ITEM_PROGRESS_CHANNEL);
    ch.postMessage({ type, createdTargetItemIds });
    ch.close();
  } catch {
    // BroadcastChannel not supported
  }
};

export const useErrorWebsocket = (userId: string, accountId?: string, itemId?: string) => {
  const { removeItemDisabled, removeItemLoading, disableOthers } = useList();
  const { mutateItemLocations } = useItemLocations();
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const addInProgressRef = useRef(false);

  const SOCKET_URL = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId}`;

  // Listen on BroadcastChannel for add-item lifecycle events so we know
  // whether an add is in progress (needed for reconnect fallback).
  useEffect(() => {
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(ADD_ITEM_PROGRESS_CHANNEL);
    } catch {
      return;
    }

    channel.onmessage = async (event: MessageEvent<{ type: string }>) => {
      const { type } = event.data ?? {};

      if (type === "add-item-started") {
        addInProgressRef.current = true;
      } else if (type === "add-item-completed" || type === "add-item-failed") {
        addInProgressRef.current = false;
      } else if (type === "add-item-timed-out") {
        if (!addInProgressRef.current) return;
        addInProgressRef.current = false;

        disableOthers(false);
        await mutateItemLocations();

        MondayApiQueryInstance.mondayToastNotification(
          "This is taking longer than expected. Your item view has been refreshed.",
          "info",
          10000,
        );
      }
    };

    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Main websocket connection
  useEffect(() => {
    const socketConnection = io(SOCKET_URL);

    setSocket(socketConnection);

    socketConnection.on("connect", () => {
      setIsConnected(true);
    });

    socketConnection.on("error", (data: SocketIOMessage) => {
      if (data.userId === userId) {
        setCurrentError(data.message);

        if (data.action === "add_item") {
          removeItemDisabled(`${data?.targetGroupId}-${data?.targetBoardId}`);
          removeItemLoading(`${data?.targetGroupId}-${data?.targetBoardId}`);
          broadcastProgressMessage("add-item-failed");
        }

        disableOthers(false);

        // Trigger Monday.com notification
        MondayApiQueryInstance.mondayToastNotification(data.message, "error", 10000);
      }
    });

    socketConnection.on("refresh_values", async (data: SocketIOMessage) => {
      if (
        data?.accountId?.toString() === accountId?.toString() &&
        data?.type === "refresh_values" &&
        data?.itemId === itemId
      ) {
        await mutateItemLocations();
      }
    });

    socketConnection.on("success", async (data: SocketIOMessage) => {
      if (data.userId?.toString() === userId.toString()) {
        setCurrentError(data.message);

        if (data.action === "add_item") {
          removeItemLoading(`${data?.targetGroupId}-${data?.targetBoardId}`);
          broadcastProgressMessage("add-item-completed", data.createdTargetItemIds ?? []);
        }

        disableOthers(false);

        await mutateItemLocations();

        MondayApiQueryInstance.mondayToastNotification(data.message, "success", 10000);
      }
    });

    // Reconnection fallback: if an add was in progress when we disconnected,
    // refresh the data on reconnect since we may have missed the success/error.
    socketConnection.io.on("reconnect", async () => {
      setIsConnected(true);
      if (!addInProgressRef.current) return;
      addInProgressRef.current = false;

      disableOthers(false);
      broadcastProgressMessage("add-item-completed");
      await mutateItemLocations();

      MondayApiQueryInstance.mondayToastNotification(
        "Connection was briefly interrupted. Your item view has been refreshed.",
        "info",
        10000,
      );
    });

    socketConnection.on("disconnect", () => {
      setIsConnected(false);
    });

    return () => {
      socketConnection.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SOCKET_URL, userId]);

  return { isConnected, currentError, socket };
};
